import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

interface Attachment {
  id: string;
  file_name: string;
  file_size: number | null;
  file_type: string | null;
  storage_path: string;
  created_at: string | null;
  report_id: string | null;
  message_id: string | null;
}

// Fetch attachments for a report
export function useReportAttachments(reportId: string | undefined) {
  return useQuery({
    queryKey: ['attachments', 'report', reportId],
    queryFn: async () => {
      if (!reportId) return [];
      
      const { data, error } = await supabase
        .from('attachments')
        .select('*')
        .eq('report_id', reportId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Attachment[];
    },
    enabled: !!reportId,
  });
}

// Fetch attachments for a message
export function useMessageAttachments(messageId: string | undefined) {
  return useQuery({
    queryKey: ['attachments', 'message', messageId],
    queryFn: async () => {
      if (!messageId) return [];
      
      const { data, error } = await supabase
        .from('attachments')
        .select('*')
        .eq('message_id', messageId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Attachment[];
    },
    enabled: !!messageId,
  });
}

// Upload attachment hook
export function useUploadAttachment() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      file,
      reportId,
      messageId
    }: {
      file: File;
      reportId?: string;
      messageId?: string;
    }) => {
      // Validate file size (50MB limit)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error(t('files.fileTooLarge', { maxSize: '50MB' }));
      }

      // Generate unique file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const storagePath = reportId 
        ? `reports/${reportId}/${fileName}`
        : `messages/${messageId}/${fileName}`;

      // Upload to Supabase Storage
      const { data: _uploadData, error: uploadError } = await supabase.storage
        .from('attachments')
        .upload(storagePath, file);

      if (uploadError) throw uploadError;

      // Create attachment record
      const { data, error } = await supabase
        .from('attachments')
        .insert({
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          storage_path: storagePath,
          report_id: reportId || null,
          message_id: messageId || null,
        })
        .select()
        .single();

      if (error) {
        // Clean up uploaded file if database insert fails
        await supabase.storage.from('attachments').remove([storagePath]);
        throw error;
      }

      return data as Attachment;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      if (data.report_id) {
        queryClient.invalidateQueries({ queryKey: ['attachments', 'report', data.report_id] });
      }
      if (data.message_id) {
        queryClient.invalidateQueries({ queryKey: ['attachments', 'message', data.message_id] });
      }
      message.success(t('files.uploadSuccess', { count: 1 }));
    },
    onError: (error: Error) => {
      console.error('Upload error:', error);
      message.error(error.message || t('files.uploadError'));
    },
  });
}

// Delete attachment hook
export function useDeleteAttachment() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (attachment: Attachment) => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('report-attachments')
        .remove([attachment.storage_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error } = await supabase
        .from('attachments')
        .delete()
        .eq('id', attachment.id);

      if (error) throw error;

      return attachment;
    },
    onSuccess: (attachment) => {
      // Invalidate relevant queries
      if (attachment.report_id) {
        queryClient.invalidateQueries({ queryKey: ['attachments', 'report', attachment.report_id] });
      }
      if (attachment.message_id) {
        queryClient.invalidateQueries({ queryKey: ['attachments', 'message', attachment.message_id] });
      }
      message.success(t('files.deleteSuccess'));
    },
    onError: (error: Error) => {
      console.error('Delete error:', error);
      message.error(t('files.deleteError'));
    },
  });
}

// Bulk upload attachments
export function useBulkUploadAttachments() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const uploadAttachment = useUploadAttachment();

  return useMutation({
    mutationFn: async ({
      files,
      reportId,
      messageId
    }: {
      files: File[];
      reportId?: string;
      messageId?: string;
    }) => {
      const results = await Promise.allSettled(
        files.map(file => 
          uploadAttachment.mutateAsync({ file, reportId, messageId })
        )
      );

      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (failed > 0) {
        throw new Error(t('files.partialUploadError', { successful, failed }));
      }

      return { successful, total: files.length };
    },
    onSuccess: ({ successful }, variables) => {
      if (variables.reportId) {
        queryClient.invalidateQueries({ queryKey: ['attachments', 'report', variables.reportId] });
      }
      if (variables.messageId) {
        queryClient.invalidateQueries({ queryKey: ['attachments', 'message', variables.messageId] });
      }
      message.success(t('files.uploadSuccess', { count: successful }));
    },
    onError: (error: Error) => {
      console.error('Bulk upload error:', error);
      message.error(error.message);
    },
  });
}

// Generate signed URL for download
export function useAttachmentUrl(attachment: Attachment | null) {
  return useQuery({
    queryKey: ['attachment-url', attachment?.id],
    queryFn: async () => {
      if (!attachment) return null;

      const { data, error } = await supabase.storage
        .from('report-attachments')
        .createSignedUrl(attachment.storage_path, 3600); // 1 hour expiry

      if (error) throw error;
      return data.signedUrl;
    },
    enabled: !!attachment,
    staleTime: 50 * 60 * 1000, // Consider fresh for 50 minutes
  });
}

// Download attachment
export function useDownloadAttachment() {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (attachmentId: string) => {
      // First get attachment details
      const { data: attachment, error: fetchError } = await supabase
        .from('attachments')
        .select('*')
        .eq('id', attachmentId)
        .single();

      if (fetchError || !attachment) throw fetchError || new Error('Attachment not found');

      // Generate signed URL
      const { data, error } = await supabase.storage
        .from('report-attachments')
        .createSignedUrl(attachment.storage_path, 3600); // 1 hour expiry

      if (error) throw error;

      // Download the file
      const response = await fetch(data.signedUrl);
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.file_name;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return attachment;
    },
    onSuccess: () => {
      message.success(t('files.downloadSuccess'));
    },
    onError: (error: Error) => {
      console.error('Download error:', error);
      message.error(t('files.downloadError'));
    },
  });
}